"use client";

import ListHeader from "@/components/design-system/list-header";
import ListPagination from "@/components/design-system/list-pagination";
import { Table, type TableColumn } from "@/components/design-system/table";
import { useEffect, useState } from "react";
import ExportDropdown, {
  ExportOption,
} from "@/components/design-system/export-dropdown";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "@store";
import {
  fetchSchoolLogs,
  selectNoSchoolLogs,
  selectSchoolLogs,
  selectSchoolLogLoading,
  selectTotalItems,
  SchoolLogModel,
} from "@/store/slices/schoolLogSlice";
import {
  CSVIcon,
  downloadFileFromBlob,
  ExcelIcon,
  FileFormats,
  hasMenuPermission,
  PDFIcon,
  ROLE_PERMISSIONS,
  successToast,
} from "@/utils/constants";
import { DownloadSchoolLogQuery } from "@/types/school-log.model";
import { downloadSchoolLogService } from "@/service/school-log.service";
import { selectUserRole } from "@/store/slices/authSlice";
import { Badge } from "@/components/design-system/badge";

// Using StudentLogModel from the slice

// Extended interface for UI display purposes
interface SchoolLogDisplay extends SchoolLogModel {
  sno?: number;
}

const ITEMS_PER_PAGE = 10;

export default function StudentLogListSection() {
  // State for search, pagination, and date range
  const [searchValue, setSearchValue] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [fromDate, setFromDate] = useState<Date | undefined>();
  const [toDate, setToDate] = useState<Date | undefined>();
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: "asc" | "desc";
  } | null>(null);

  // Redux hooks
  const dispatch = useDispatch<AppDispatch>();
  const schoolLogs = useSelector(selectSchoolLogs);
  const loading = useSelector(selectSchoolLogLoading);
  const totalItems = useSelector(selectTotalItems);
  const noSchoolLogs = useSelector(selectNoSchoolLogs);
  const userRole = useSelector(selectUserRole);

  const columns: TableColumn<SchoolLogDisplay>[] = [
    {
      key: "sno",
      title: "S.No",
      dataIndex: "sno",
      minWidth: 68,
      maxWidth: 68,
      align: "center",
      render: (_, __, index) => {
        // Calculate S.No based on current page and index
        const sno = (currentPage - 1) * ITEMS_PER_PAGE + index + 1;
        return (
          <div className="text-[#1F1F1F] font-medium text-center text-borderBlack">
            {String(sno).padStart(2, "0")}
          </div>
        );
      },
    },
    {
      key: "schoolName",
      title: "School Name",
      dataIndex: "school_name",
      minWidth: 278,
      maxWidth: 278,
      sortable: true,
      render: (_, record) => (
        <div
          className="text-[#1F1F1F] font-medium"
          style={{
            overflowWrap: "anywhere",
            whiteSpace: "pre-wrap",
          }}
        >
          {record.school_name}
        </div>
      ),
    },
    {
      key: "grade",
      title: "Grade",
      dataIndex: "grades",
      minWidth: 148,
      maxWidth: 148,
      sortable: true,
      render: (_, record) => (
        <div className="text-[#1F1F1F] font-medium">{record.grades}</div>
      ),
    },
    {
      key: "schoolAdminName",
      title: "User",
      dataIndex: "username",
      minWidth: 222,
      maxWidth: 222,
      sortable: true,
      render: (_, record) => (
        <div
          className="text-[#1F1F1F] font-medium"
          style={{
            overflowWrap: "anywhere",
            whiteSpace: "pre-wrap",
          }}
        >
          {record.username}
        </div>
      ),
    },
    {
      key: "emailAddress",
      title: "Email Address",
      dataIndex: "email",
      minWidth: 249,
      maxWidth: 249,
      sortable: true,
      render: (_, record) => (
        <div
          className="text-[#1F1F1F] font-medium"
          style={{
            overflowWrap: "anywhere",
            whiteSpace: "pre-wrap",
          }}
        >
          {record.email}
        </div>
      ),
    },
    {
      key: "contactNumber",
      title: "Contact Number",
      dataIndex: "contact_number",
      minWidth: 190,
      maxWidth: 190,
      sortable: true,
      render: (_, record) => (
        <div className="text-[#1F1F1F] font-medium">
          {record.contact_number}
        </div>
      ),
    },
    {
      key: "ipAddress",
      title: "IP Address",
      dataIndex: "ip",
      minWidth: 170,
      maxWidth: 170,
      sortable: true,
      render: (_, record) => (
        <div className="text-[#1F1F1F] font-medium">{record.ip}</div>
      ),
    },
    {
      key: "geoLocation",
      title: "Geo Location",
      dataIndex: "location",
      minWidth: 170,
      maxWidth: 170,
      sortable: true,
      render: (_, record) => (
        <div
          className="text-[#1F1F1F] font-medium"
          style={{
            overflowWrap: "anywhere",
            whiteSpace: "pre-wrap",
          }}
        >
          {record.location}
        </div>
      ),
    },
    {
      key: "safeLocation",
      title: "Safe Area Login",
      dataIndex: "is_within_geofence",
      minWidth: 200,
      maxWidth: 200,
      // sortable: true,
      render: (_, record) => (
        <Badge variant={record.is_within_geofence ? "success" : "danger"}>
          {record.is_within_geofence ? "In Zone" : "Out of Zone"}
        </Badge>
      ),
    },
    {
      key: "loginDate",
      title: "Login Date",
      dataIndex: "login_date",
      minWidth: 170,
      maxWidth: 170,
      sortable: true,
      render: (_, record) => (
        <div className="text-[#1F1F1F] font-medium">{record.login_date}</div>
      ),
    },
    {
      key: "logoutDate",
      title: "Logout Date",
      dataIndex: "logout_date",
      minWidth: 170,
      maxWidth: 170,
      sortable: true,
      render: (_, record) => (
        <div className="text-[#1F1F1F] font-medium">{record.logout_date}</div>
      ),
    },
    {
      key: "loginTime",
      title: "Login Time",
      dataIndex: "login_time",
      minWidth: 170,
      maxWidth: 170,
      sortable: true,
      render: (_, record) => (
        <div className="text-[#1F1F1F] font-medium">{record.login_time}</div>
      ),
    },
    {
      key: "logoutTime",
      title: "Logout Time",
      dataIndex: "logout_time",
      minWidth: 170,
      maxWidth: 170,
      sortable: true,
      render: (_, record) => (
        <div className="text-[#1F1F1F] font-medium">{record.logout_time}</div>
      ),
    },
    {
      key: "sessionHours",
      title: "Session Hours",
      dataIndex: "duration",
      minWidth: 180,
      maxWidth: 180,
      sortable: true,
      render: (_, record) => (
        <div className="text-[#1F1F1F] font-medium">{record.duration}</div>
      ),
    },
  ];

  // Handle date range changes from the ListHeader component
  const handleDateRangeChange = (
    startDate: Date | undefined,
    endDate: Date | undefined
  ) => {
    setFromDate(startDate);
    setToDate(endDate);
    // Reset to first page when date range changes
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  };

  const handleSort = (
    config: { key: string; direction: "asc" | "desc" } | null
  ) => {
    setSortConfig(config);
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  };

  // NME-Schooladminname, GRDE-Grade, EML - Email, CNUM-contact number, SCH-school name, IP-ip address, LOC-Location, LDT-login date, LODT-logoutdate, LTIM- logintime, LTOM-logout time, DUR-duration

  // Map column keys to API sorting keys
  const getSortField = (key: string): string => {
    const sortFieldMap: Record<string, string> = {
      schoolName: "SCH", // Name
      grade: "GRDE", // Grade
      emailAddress: "EML", // Email
      contactNumber: "CNUM",
      schoolAdminName: "NME", // School name
      ipAddress: "IP", // IP address
      geoLocation: "LOC", // Location
      loginDate: "LDT", // Login date
      logoutDate: "LODT", // Logout date
      loginTime: "LTIM", // Login time
      logoutTime: "LTOM", // Logout time
      sessionHours: "DUR", // Duration
    };

    return sortFieldMap[key] || key;
  };

  // Fetch student logs when component mounts or filters change
  useEffect(() => {
    // Reset to first page when search value changes
    if (searchValue && currentPage !== 1) {
      setCurrentPage(1);
      return; // The page change will trigger another fetch
    }

    const sortParams = sortConfig
      ? {
          sortBy: getSortField(sortConfig.key),
        }
      : {};

    dispatch(
      fetchSchoolLogs({
        page: currentPage,
        pageSize: ITEMS_PER_PAGE,
        search: searchValue,
        fromDate: fromDate,
        toDate: toDate,
        ...sortParams,
      })
    );
  }, [dispatch, currentPage, searchValue, fromDate, toDate, sortConfig]);

  // Handle search input changes
  const handleSearchChange = (value: string) => {
    setSearchValue(value);
  };

  // Handle page change
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Handle export of time entries as Excel or PDF
  const handleExport = async (type: "xl" | "pdf" | "csv") => {
    try {
      // Create query params from current filters
      const queryParams: DownloadSchoolLogQuery = {
        search: searchValue || undefined,
        fromDate: fromDate,
        toDate: toDate,
      };

      const response = await downloadSchoolLogService(type, queryParams);

      downloadFileFromBlob(response, type, "assignment_report");

      successToast(
        "Export Successful",
        `Assignments exported as ${FileFormats[type].name} successfully`
      );
    } catch (error) {
      console.error(`Error exporting schools as ${type}:`, error);
    }
  };

  // Export options
  const exportOptions: ExportOption[] = [
    {
      label: "PDF",
      icon: <PDFIcon />,
      action: () => handleExport("pdf"),
    },
    {
      label: "Excel",
      icon: <ExcelIcon />,
      action: () => handleExport("xl"),
    },
    {
      label: "CSV",
      icon: <CSVIcon />,
      action: () => handleExport("csv"),
    },
  ];

  if (noSchoolLogs) {
    return (
      <div className="bg-white rounded-[12px] px-4 sm:px-6 md:px-[32px] w-full overflow-hidden">
        <div
          className={`flex flex-col h-[calc(100vh-140px)] mb-2 overflow-x-auto justify-center items-center`}
        >
          {loading ? (
            <>
              <p className="text-[#1F1F1F] text-[24px] font-medium mb-[28px]">
                Loading...
              </p>
            </>
          ) : (
            <>
              <p className="text-[#1F1F1F] text-[24px] font-bold mb-[9px]">
                No school log available.
              </p>

              <p className="text-[#1F1F1F] text-[24px] font-medium mb-[28px]">
                School's Login activity will appear here.
              </p>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-[12px] px-4 sm:px-6 md:px-[32px] w-full overflow-hidden h-full flex flex-1 flex-col">
      {/* List Header with Search, Date Filter, and Export */}
      <ListHeader
        title="School Log List"
        searchValue={searchValue}
        onSearchChange={handleSearchChange}
        onDateRangeChange={handleDateRangeChange}
        // rightAction={<ExportDropdown options={exportOptions} />}
        rightAction={
          <>
            {hasMenuPermission(
              ROLE_PERMISSIONS.BUTTON_EXPORT_SCHOOL_LOG,
              userRole?.permissions as string[]
            ) && <ExportDropdown options={exportOptions} />}
          </>
        }
      />

      {/* Table Container with fixed header */}
      <div className="flex flex-1 flex-col border border-[#EDEDED] rounded-md overflow-x-auto">
        <div className="flex-1 overflow-auto h-full flex">
          {/* {loading ? (
            <div className="flex items-center justify-center h-full w-full">
              <div className="flex flex-col items-center">
                <div className="w-12 h-12 border-4 border-t-[#5C88C5] border-r-[#5C88C5] border-b-[#5C88C5] border-l-transparent rounded-full animate-spin"></div>
                <p className="mt-4 text-gray-600">Loading schools...</p>
              </div>
            </div>
          ) : ( */}
          <Table
            columns={columns}
            data={schoolLogs} // Use data from Redux store
            rowKey="id"
            showHeader={true} // Hide header as we're showing it separately above
            size="middle"
            sticky
            scroll={{ x: 1200 }}
            loading={loading} // We're handling loading state ourselves
            sortConfig={sortConfig || undefined}
            onSort={handleSort}
            // className="body-only-table"
          />
          {/* )} */}
        </div>
      </div>

      {/* Pagination */}
      <div className="mt-4">
        <ListPagination
          currentPage={currentPage}
          setCurrentPage={handlePageChange}
          totalItems={totalItems}
          itemsPerPage={ITEMS_PER_PAGE}
          itemsLabel="Schools Logs"
        />
      </div>
    </div>
  );
}
